import {Component, Input, OnChanges, OnInit} from '@angular/core';
import {IntelligenceData} from '../../../models/intelligence-data.interface';
import {Chart} from 'angular-highcharts';
import {PointOptionsObject, SeriesOptionsType} from 'highcharts';
import {BENCHMARK_BY_YEAR_COLORS} from '../../../../constants/charts';
import {MessageService} from '../../../../shared/services/message.service';
import {Router} from '@angular/router';
import {AuthService} from '../../../../core/services/auth.service';
import {Countries} from '../../../../admin/models/countries.interface';

interface EWasteLegislationData {
  category: string;
  intelligenceData: IntelligenceData[];
}

@Component({
  selector: 'app-e-waste-legislation-bar-chart',
  templateUrl: './e-waste-legislation-bar-chart.component.html',
  styleUrls: ['./e-waste-legislation-bar-chart.component.scss']
})
export class EWasteLegislationBarChartComponent implements OnInit, OnChanges {

  textBelow!: string;
  chart!: Chart;
  chartData!: SeriesOptionsType[];
  countryCodes: string[] = [];
  countries: Countries[] = [];
  textBelowCode = 'intelligence.Country.EWasteLegislation.textBelow';
  isAdmin!: boolean;
  totalResponsesSmallEquipament!: number;
  totalResponsesIT!: number;
  totalResponsesLamps!: number;
  totalResponsesLargeEquipament!: number;
  totalResponsesPVPanels!: number;
  totalResponsesScreens!: number;
  totalResponsesTEE!: number;

  @Input()
  title!: string;
  @Input()
  subtitle!: string;
  @Input()
  data!: IntelligenceData[];
  @Input()
  categoriesFilter?: string[];

  private static getCountryCodes(chartData: IntelligenceData[]): string[] {
    const chartCodes = chartData?.map(item => item.countryCode);
    return [...new Set(chartCodes)];
  }

  private static getCountries(chartData: IntelligenceData[]): Countries[] {
    const countries = chartData?.map(item => {
      return {code: item.countryCode, name: item.countryName};
    });
    return [...new Set(countries)];
  }

  constructor(private readonly messageService: MessageService,
              private readonly router: Router,
              private authService: AuthService) {
  }

  ngOnInit(): void {
    this.isAdmin = this.authService.getUserRoles().includes('WF_ADMIN');
    this.messageService.getDialogInfo(this.textBelowCode).subscribe((data) => {
      if (data !== null) {
        this.textBelow = data.description;
      }
    });
  }

  ngOnChanges(): void {
    if (this.data) {
      this.countryCodes = EWasteLegislationBarChartComponent.getCountryCodes(this.data);
      this.countries = EWasteLegislationBarChartComponent.getCountries(this.data);
      this.chartData = this.convertToChartData(this.groupBy(this.splitCategories(this.data), 'categoryName'));

      this.chart = new Chart({
        chart: {
          zoomType: 'xy',
          type: 'column',
          options3d: {
            enabled: true,
            alpha: 7,
            beta: 0,
            depth: 35,
            viewDistance: 15
          }
        },
        colors: BENCHMARK_BY_YEAR_COLORS,
        title: {
          text: this.title
        },
        subtitle: {
          text: this.subtitle
        },
        plotOptions: {
          column: {
            stacking: 'normal',
          }
        },
        exporting: {
          enabled: true
        },
        tooltip: {
          formatter(): string[] | undefined {
            if (this.point.name) {
              const pointOptions = this.point as PointOptionsObject;
              if (pointOptions.custom?.absolute === 0) {
                return [this.point.name];
              }
              return ['<div class="center"><b>' + this.point.name + '</b></div><br><br>',
                '- Category: ' + this.point.series.name + '<br>',
                '- Total with this cat. : ' + pointOptions?.custom?.totalResponses + '<br>',
                '- Total respondents: ' + pointOptions?.custom?.totalRespondents + '<br>',

              ];
            }
            // If not null, use the default formatter
            // return tooltip.defaultFormatter.call(this, tooltip);
            return undefined;
          }
        },
        xAxis: {
          categories: this.countryCodes,
          title: {
            text: 'Country',
          }
        },
        yAxis: {
          labels: {
            enabled: false
          }
        },
        series: this.chartData
      });
    }
  }

  editTextBelow(): void {
    this.router.navigate(['/admin/chart-labels'], {state: {code: this.textBelowCode}}).then(
      () => {
        // do nothing
      }
    );
  }

  private convertToChartData(data: EWasteLegislationData[]): SeriesOptionsType[] {
    this.chart?.ref?.hideData();
    const chartData = [] as SeriesOptionsType[];
    data.sort((a, b) => (a.category > b.category) ? 1 : ((b.category > a.category) ? -1 : 0));
    data.map(item => {
      const newItem = {} as EWasteLegislationData;
      newItem.category = item.category;
      newItem.intelligenceData = this.countries.map(country => {
        let newDataItem: IntelligenceData;
        const dataItem = item.intelligenceData.find(element => element.countryCode === country.code);
        if (dataItem) {
          newDataItem = {...dataItem};
        } else {
          newDataItem = {
            answer: '',
            categoryId: 0,
            categoryName: '',
            countryName: country.name,
            lastModifiedDate: new Date(),
            questionCode: '',
            questionDescription: '',
            questionId: 0,
            totalAdditionResponses: 0,
            totalNA: 0,
            totalNo: 0,
            totalRespondents: 0,
            totalSum: 0,
            totalYes: 0,
            countryCode: country.code,
            year: this.data[0].year,
            totalResponses: 0
          };
        }
        return newDataItem;
      });
      return newItem;
    }).filter(item => !this.categoriesFilter || this.categoriesFilter?.includes(item.category))
      .forEach(item => {
        let responses = 0;
        switch(item.category) {
          case 'Small equipment' || 'D. Small eq.':
            responses = this.totalResponsesSmallEquipament;
            break;
          case 'IT' || 'C. Small IT':
            responses = this.totalResponsesIT;
            break;
          case 'Lamps ' || 'F. Lamps':
            responses = this.totalResponsesLamps;
            break;
          case 'Large equipment' || 'A. Large eq.':
            responses = this.totalResponsesLargeEquipament;
            break;
          case 'PV panels' || 'G. PV Panels':
            responses = this.totalResponsesPVPanels;
            break;
          case 'Screens' || 'E. screens':
            responses = this.totalResponsesScreens;
            break;
          case 'TEE' || 'B. TEE':
            responses = this.totalResponsesTEE;
            break;
        }
        if (!this.categoriesFilter || this.categoriesFilter.includes(item.category)) {
          chartData.push({
            name: item.category,
            type: 'column',
            data: item.intelligenceData.map(dataItem => {
              return {
                y: dataItem.totalResponses ? 1 : 0,
                name: dataItem.countryName + ' - ' + dataItem.year,
                custom: {totalResponses: responses, totalRespondents: this.countryCodes.length}
              };
            })
          });
        }
      });
    return chartData;
  }

  private groupBy = (list: any, key: any) => {
    const groupedList = list.reduce((rv: any, x: any) => {
      (rv[x[key]] = rv[x[key]] || []).push(x);
      return rv;
    }, {});
    const yearIntelligenceDataArray: EWasteLegislationData[] = [];
    Object.getOwnPropertyNames(groupedList).forEach((groupKey: string) => {
      const data = {} as EWasteLegislationData;
      data.category = groupKey;
      data.intelligenceData = groupedList[groupKey];
      yearIntelligenceDataArray.push(data);
    });
    return yearIntelligenceDataArray;
  }


  private splitCategories(data: IntelligenceData[]): IntelligenceData[] {
    const splittedData = [] as IntelligenceData[];
    data.forEach(item => {
      const arrayCategories = item.categoryName.split(';').filter(cat => cat !== '').sort();
      const filteredCategories = arrayCategories.filter(cat => !this.categoriesFilter || this.categoriesFilter.includes(cat));
      arrayCategories.forEach(category => splittedData.push({
          ...item,
          categoryName: category,
          totalAdditionResponses: arrayCategories.length,
          totalResponses: filteredCategories.length
      }));
    });
    this.totalResponsesSmallEquipament = splittedData.filter(data => data.categoryName == 'Small equipment' || data.categoryName == 'D. Small eq.').length;
    this.totalResponsesIT = splittedData.filter(data => data.categoryName == 'IT' || data.categoryName == 'C. Small IT').length;
    this.totalResponsesLamps = splittedData.filter(data => data.categoryName == 'Lamps ' || data.categoryName == 'F. Lamps').length;
    this.totalResponsesLargeEquipament = splittedData.filter(data => data.categoryName == 'Large equipment' || data.categoryName == 'A. Large eq.').length;
    this.totalResponsesPVPanels = splittedData.filter(data => data.categoryName == 'PV panels' || data.categoryName == 'G. PV panels').length;
    this.totalResponsesScreens = splittedData.filter(data => data.categoryName == 'Screens' || data.categoryName == 'E. screens').length;
    this.totalResponsesTEE = splittedData.filter(data => data.categoryName == 'TEE' || data.categoryName == 'B. TEE').length;
    return splittedData;
  }
}
